import Button from '@rezdy/components/Button';
import Flexbox from '@rezdy/components/Flexbox';
import Form from '@rezdy/components/Form';
import Field from '@rezdy/components/Form/Field';
import InputCheck, { IInputCheckOnChange } from '@rezdy/components/Form/FormElements/InputCheck';
import PhoneInput from '@rezdy/components/Form/FormElements/PhoneInput';
import RadioInput from '@rezdy/components/Form/FormElements/RadioInput';
import CountrySelect from '@rezdy/components/Form/FormElements/selects/CountrySelect';
import TextInput from '@rezdy/components/Form/FormElements/TextInput';
import Grid from '@rezdy/components/Grid';
import { H2 } from '@rezdy/components/Heading';
import Link from '@rezdy/components/Link';
import TextStyle from '@rezdy/components/TextStyle';
import qs from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import constants from '../../constants/auth';
import routes from '../../constants/routes';
import { blue, red } from '../../constants/theme';
import auth from '../../services/auth';
import { getRecaptcha } from '../../services/recaptcha';
import routing from '../../services/routing';
import CreatePassword from '../CreatePassword';
import { ErrorMessage } from '../ErrorMessage';
import { MarketingLayout } from '../Layout/MarketingLayout';
import Message from '../Message';
import { RequiredFieldLabel } from '../RequiredFieldLabel';

type BusinessType = 'OPERATOR' | 'RESELLER';
const businessTypes: { label: string; value: BusinessType }[] = [
    { label: "I'm an Operator", value: 'OPERATOR' },
    { label: "I'm a Reseller", value: 'RESELLER' },
];
interface FormFields {
    email: string;
    firstName: string;
    lastName: string;
    companyName: string;
    companyWebsite?: string;
    companyPhone: string;
    countryCode: string;
    password: string;
}
const fieldLabels: { [field: string]: string } = {
    email: 'Email',
    firstName: 'First Name',
    lastName: 'Last Name',
    companyName: 'Business Name',
    companyWebsite: 'Website',
    companyPhone: 'Phone',
    countryCode: 'Country',
    password: 'Password',
};
const requiredFields = [
    'firstName',
    'lastName',
    'email',
    'companyName',
    'companyWebsite',
    'companyPhone',
    'countryCode',
    'password',
];

/**
 * @deprecated
 */
const SignUpWithEmail = () => {
    const [clientSideErrors, setClientSideErrors] = useState<string>();
    const [missingFields, setMissingFields] = useState<string[]>([]);
    const [serverErrorCode, setServerErrorCode] = useState<string>();

    const [agreeToTCs, setAgreeToTCs] = useState<boolean>(false);
    const [vendorAgreementAccepted, setVendorAgreementAccepted] = useState<boolean>(false);
    const [businessType, setBusinessType] = useState<BusinessType>('OPERATOR');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const phoneRef = useRef<PhoneInput>(null);
    const countryRef = useRef<CountrySelect>(null);

    useEffect(() => {
        getRecaptcha().render();
    }, []);

    const signUp = async (formFields: FormFields) => {
        // Reset form states
        setClientSideErrors(undefined);
        setServerErrorCode(undefined);
        setMissingFields([]);

        // Validate required fields
        const missingFields = requiredFields.filter(
            // The default unfilled PhoneInput component value is '+'
            field =>
                (field === 'companyPhone' && ['', '+'].includes(formFields.companyPhone)) ||
                !formFields[field as keyof FormFields]
        );
        if (missingFields.length > 0) {
            setClientSideErrors('Please fill in the missing field(s)');
            setMissingFields(missingFields);
            return;
        }
        if (!agreeToTCs) {
            setClientSideErrors('Please agree to our terms of use and privacy policy');
            return;
        }
        if (businessType === 'RESELLER' && !vendorAgreementAccepted) {
            setClientSideErrors('Please accept the vendor agreement');
            return;
        }
        const queryParams = qs.parse(routing.location.search);
        try {
            setIsSubmitting(true);
            const recaptchToken = await getRecaptcha().execute();
            await auth.signUpV3({
                ...formFields,
                recaptcha: constants.RECAPTCHA_ENABLED !== 'false',
                token: recaptchToken,
                companyWebsite: formFields.companyWebsite,
                isOperator: businessType === 'OPERATOR',
                utmSource: queryParams.utm_source as string,
                utmCampaign: queryParams.utm_campaign as string,
                utmMedium: queryParams.utm_medium as string,
                utmContent: queryParams.utm_content as string,
                utmTerm: queryParams.utm_term as string,
            });
            setIsSubmitting(false);
            getRecaptcha().unmount();
            routing.push(routes.SIGN_UP_COMPLETE_EMAIL.replace(':version', '3'), {
                username: encodeURIComponent(formFields.email),
            });
        } catch (err) {
            setServerErrorCode(err.message);
        }
        setIsSubmitting(false);
    };

    const renderForm = () => {
        return (
            <Form onSubmit={signUp} refs={[phoneRef, countryRef]} novalidate>
                <H2>
                    Try Rezdy <span style={{ color: blue }}>free</span>
                </H2>
                <Flexbox justifyContent="space-between" style={{ marginBottom: '24px' }}>
                    <TextStyle as="div" style={{ margin: 0 }}>
                        No credit card required.
                    </TextStyle>
                    <TextStyle as="div" style={{ margin: 0, color: red }}>
                        * All fields required
                    </TextStyle>
                </Flexbox>
                {clientSideErrors && (
                    <Message as="p" align="center" isDirty>
                        {clientSideErrors}
                    </Message>
                )}
                {serverErrorCode && <ErrorMessage as="p" align="center" isDirty i18nKey={serverErrorCode} />}
                <Grid.Container rowGap="24px">
                    <Flexbox column>
                        <RequiredFieldLabel
                            label={fieldLabels.firstName}
                            missing={missingFields.includes('firstName')}
                        />
                        <Field name="firstName">
                            <TextInput />
                        </Field>
                    </Flexbox>
                    <Flexbox column>
                        <RequiredFieldLabel label={fieldLabels.lastName} missing={missingFields.includes('lastName')} />
                        <Field name="lastName">
                            <TextInput />
                        </Field>
                    </Flexbox>
                    <Flexbox column>
                        <RequiredFieldLabel label={fieldLabels.email} missing={missingFields.includes('email')} />
                        <Field name="email">
                            <TextInput />
                        </Field>
                    </Flexbox>
                    <Flexbox column>
                        <RequiredFieldLabel
                            label={fieldLabels.companyPhone}
                            missing={missingFields.includes('companyPhone')}
                        />
                        <Field name="companyPhone">
                            <PhoneInput ref={phoneRef} />
                        </Field>
                    </Flexbox>
                    <Flexbox column>
                        <RequiredFieldLabel
                            label={fieldLabels.countryCode}
                            missing={missingFields.includes('countryCode')}
                        />
                        <Field name="countryCode">
                            <CountrySelect ref={countryRef} />
                        </Field>
                    </Flexbox>
                    <RadioInput
                        options={businessTypes}
                        onChange={opt => setBusinessType(opt.value as BusinessType)}
                        cols="repeat(auto-fit, minmax(180px, 1fr))"
                        colGap="8px"
                        rowGap="8px"
                        defaultValue="OPERATOR"
                        id="businessType"
                    />
                    <Flexbox column>
                        <RequiredFieldLabel
                            label={fieldLabels.companyName}
                            missing={missingFields.includes('companyName')}
                        />
                        <Field name="companyName">
                            <TextInput />
                        </Field>
                    </Flexbox>
                    <Flexbox column>
                        <RequiredFieldLabel
                            label={fieldLabels.companyWebsite}
                            missing={missingFields.includes('companyWebsite')}
                        />
                        <Field name="companyWebsite">
                            <TextInput placeholder="example.com" />
                        </Field>
                    </Flexbox>
                    <CreatePassword
                        customLabel={
                            <RequiredFieldLabel
                                label={fieldLabels.password}
                                missing={missingFields.includes('password')}
                            />
                        }
                    />
                    <InputCheck
                        inlineLabel={
                            <TextStyle weight="bold" size="small">
                                By creating an account, you agree to our{' '}
                                <Link href="https://rezdy.com/terms-of-use/" target="_blank">
                                    {'terms of use'}
                                </Link>
                                {' and '}
                                <Link href="https://rezdy.com/privacy-policy/" target="_blank">
                                    {'privacy policy'}
                                </Link>
                            </TextStyle>
                        }
                        value={agreeToTCs}
                        onChange={(props: IInputCheckOnChange) => setAgreeToTCs(props.value)}
                    />
                    {businessType === 'RESELLER' && (
                        <InputCheck
                            id={'vendorAgreementAccepted'}
                            inlineLabel={
                                <TextStyle weight="bold" size="small">
                                    I agree to{' '}
                                    <Link href="https://www.rezdy.com/vendor-agreement/" target="_blank">
                                        {"Rezdy's Vendor Agreement"}
                                    </Link>
                                </TextStyle>
                            }
                            value={vendorAgreementAccepted}
                            onChange={(props: IInputCheckOnChange) => setVendorAgreementAccepted(props.value)}
                        />
                    )}
                    <Button
                        type="submit"
                        action="primary"
                        loading={isSubmitting}
                        width="100%"
                        tight
                        name="customer-signup-complete"
                    >
                        Start free trial
                    </Button>
                </Grid.Container>
            </Form>
        );
    };

    return <MarketingLayout>{renderForm()}</MarketingLayout>;
};

export default SignUpWithEmail;
